// Single source of truth for "Other Task" configuration across every department.
// All 8 *_other_tasks tables are byte-for-byte identical. The only real
// differences across the UI are cosmetic (time column style + type header label)
// and the small set of task types each dept exposes in its dropdown.

export const BASE_OTHER_TASK_TYPES = [
  { value: "laptop_issue", label: "Laptop Issue" },
  { value: "software_error", label: "Software Error" },
  { value: "meetings", label: "Meetings" },
  { value: "fun_friday", label: "Fun Friday" },
  { value: "research", label: "Research" },
  { value: "others", label: "Others" },
] as const;

export const DEPT_EXTRA_TASK_TYPES: Record<
  string,
  { value: string; label: string }[]
> = {
  editor: [{ value: "network_issue", label: "Network Issue" }],
  graphic_designer: [{ value: "storage_issue", label: "Storage Issue" }],
  ai: [{ value: "storage_issue", label: "Storage Issue" }],
  marketer: [{ value: "network_issue", label: "Network Issue" }],
  seo: [
    { value: "tool_subscription_issue", label: "Tool Subscription Issue" },
  ],
  scriptwriter: [],
  smm: [],
  calling: [],
  admin: [],
};

export function getOtherTaskTypes(department: string) {
  const extras = DEPT_EXTRA_TASK_TYPES[department] ?? [];
  return [...BASE_OTHER_TASK_TYPES, ...extras];
}

export function getOtherTaskLabel(department: string, value: string): string {
  const match = getOtherTaskTypes(department).find((t) => t.value === value);
  return match?.label ?? value;
}

export type TimeColumnStyle = "combined" | "split";

export interface OtherTasksTableConfig {
  tableName: string;
  timeColumnStyle: TimeColumnStyle;
  typeColumnLabel: string;
  /** React-Query key for invalidation of this department's other-task list. */
  queryKey: string;
}

export const OTHER_TASKS_TABLE_CONFIG: Record<string, OtherTasksTableConfig> = {
  editor: {
    tableName: "editor_other_tasks",
    timeColumnStyle: "combined",
    typeColumnLabel: "TASK TYPE",
    queryKey: "editor-other-tasks",
  },
  graphic_designer: {
    tableName: "designer_other_tasks",
    timeColumnStyle: "combined",
    typeColumnLabel: "TASK TYPE",
    queryKey: "designer-other-tasks",
  },
  ai: {
    tableName: "ai_other_tasks",
    timeColumnStyle: "combined",
    typeColumnLabel: "TASK TYPE",
    queryKey: "ai-other-tasks",
  },
  scriptwriter: {
    tableName: "scriptwriter_other_tasks",
    timeColumnStyle: "split",
    typeColumnLabel: "TASK TYPE",
    queryKey: "scriptwriter-other-tasks",
  },
  calling: {
    tableName: "calling_other_tasks",
    timeColumnStyle: "split",
    typeColumnLabel: "TASK TYPE",
    queryKey: "calling-other-tasks",
  },
  marketer: {
    tableName: "marketer_other_tasks",
    timeColumnStyle: "split",
    typeColumnLabel: "TYPE",
    queryKey: "marketer-other-tasks",
  },
  seo: {
    tableName: "seo_other_tasks",
    timeColumnStyle: "split",
    typeColumnLabel: "TYPE",
    queryKey: "seo-other-tasks",
  },
  smm: {
    tableName: "smm_other_tasks",
    timeColumnStyle: "split",
    typeColumnLabel: "TYPE",
    queryKey: "smm-other-tasks",
  },
  admin: {
    tableName: "admin_other_tasks",
    timeColumnStyle: "split",
    typeColumnLabel: "TYPE",
    queryKey: "admin-other-tasks",
  },
};

export function getOtherTasksConfig(
  department: string,
): OtherTasksTableConfig | null {
  return OTHER_TASKS_TABLE_CONFIG[department] ?? null;
}
